/**
 * The editor's form state, and the patch it turns into.
 *
 * Two rules shape everything here.
 *
 * **Only what actually changed is sent.** `contracts/API.md` § Seller deal edit shape makes
 * `changedFields` "only the fields whose value actually differs from the stored one", so a
 * form that PATCHed every input would be asking core to re-evaluate a deal the founder did
 * not touch. Comparison happens against the values core sent, after the same trimming core
 * applies, so a stray space is not an edit.
 *
 * **Local checks answer quickly; they are never the authority.** The only checks here are
 * the two a request cannot even express — a price that is not a number has no cents to
 * send, and a deal type must be one of the values core accepts. Length, URL scheme,
 * requiredness and canon § 2's cross-field rule all belong to core, which evaluates the
 * last one against the *merged* deal and names the offending field in its answer. A second
 * copy of those rules in this file would be a second thing to keep true.
 */
import { parsePriceAmount } from "./money";
import type { EditableDealValues } from "../api/deal-edit";
import type { DealEditPatch, DealType, EditableDealField } from "../api/types";

/** The five values core accepts, and the words the select shows for them. */
export const DEAL_TYPE_OPTIONS: readonly { value: DealType; label: string }[] = [
  { value: "lifetime", label: "Lifetime deal (LTD)" },
  { value: "discount", label: "Discount on a plan" },
  { value: "credits", label: "Extra credits or usage" },
  { value: "founder_offer", label: "Founder or launch offer" },
  { value: "other", label: "Something else" },
];

const DEAL_TYPE_VALUES = new Set<string>(DEAL_TYPE_OPTIONS.map((option) => option.value));

/**
 * Every control's value as the DOM holds it: strings and one boolean.
 *
 * Money is a string here rather than a number because that is what a founder types, and
 * because an emptied box has to survive as "cleared" rather than collapsing to zero — canon
 * makes a price nullable, and `$0.00` is a very different claim from "no price stated".
 */
export interface DealFormValues {
  productName: string;
  productUrl: string;
  logoUrl: string;
  shortDescription: string;
  dealUrl: string;
  dealType: string;
  originalPrice: string;
  dealPrice: string;
  valueNotes: string;
  couponCode: string;
  limitations: string;
  isExclusive: boolean;
}

/** Dollars for a money box, from core's integer cents. Empty when the deal states none. */
function priceInput(cents: number | null): string {
  if (cents === null) return "";
  const whole = Math.trunc(cents / 100);
  const remainder = cents % 100;
  return remainder === 0
    ? String(whole)
    : `${String(whole)}.${String(remainder).padStart(2, "0")}`;
}

/** The form, filled from the deal core sent. */
export function formValuesOf(values: EditableDealValues): DealFormValues {
  return {
    productName: values.productName,
    productUrl: values.productUrl,
    logoUrl: values.logoUrl ?? "",
    shortDescription: values.shortDescription,
    dealUrl: values.dealUrl,
    dealType: values.dealType ?? "",
    originalPrice: priceInput(values.originalPriceCents),
    dealPrice: priceInput(values.dealPriceCents),
    valueNotes: values.valueNotes ?? "",
    couponCode: values.couponCode ?? "",
    limitations: values.limitations ?? "",
    isExclusive: values.isExclusive,
  };
}

export type FieldErrors = Partial<Record<EditableDealField, string>>;

export type DealPatchResult =
  | { ok: true; patch: DealEditPatch; changedFields: EditableDealField[] }
  | { ok: false; fieldErrors: FieldErrors };

/** An optional text box: emptied means cleared, which is an explicit `null` to core. */
function optionalText(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * The difference between the deal core sent and what is in the form.
 *
 * An unchanged form yields an empty patch rather than an error. Core refuses an empty body —
 * "nothing was sent, and nothing happened" is indistinguishable from a UI that dropped the
 * founder's changes — and the editor says so itself rather than spending a request to be
 * told.
 */
export function buildDealPatch(
  current: EditableDealValues,
  form: DealFormValues,
): DealPatchResult {
  const fieldErrors: FieldErrors = {};

  const originalPrice = parsePriceAmount(form.originalPrice);
  if (!originalPrice.ok) fieldErrors.originalPriceCents = originalPrice.message;
  const dealPrice = parsePriceAmount(form.dealPrice);
  if (!dealPrice.ok) fieldErrors.dealPriceCents = dealPrice.message;

  const dealType = form.dealType.trim();
  if (dealType !== "" && !DEAL_TYPE_VALUES.has(dealType)) {
    fieldErrors.dealType = "Choose one of the deal types listed.";
  } else if (dealType === "" && current.dealType !== null) {
    // Core requires a deal type on every deal, and clearing a select is not a way to say
    // anything: there is no "no deal type" to send.
    fieldErrors.dealType = "Choose a deal type.";
  }

  if (Object.keys(fieldErrors).length > 0) return { ok: false, fieldErrors };

  const patch: DealEditPatch = {};
  const changedFields: EditableDealField[] = [];

  const put = <K extends EditableDealField & keyof DealEditPatch>(
    field: K,
    next: DealEditPatch[K],
    previous: unknown,
  ): void => {
    if (next === previous) return;
    patch[field] = next;
    changedFields.push(field);
  };

  put("productName", form.productName.trim(), current.productName);
  put("productUrl", form.productUrl.trim(), current.productUrl);
  put("logoUrl", optionalText(form.logoUrl), current.logoUrl);
  put("shortDescription", form.shortDescription.trim(), current.shortDescription);
  put("dealUrl", form.dealUrl.trim(), current.dealUrl);
  if (dealType !== "") put("dealType", dealType as DealType, current.dealType);
  put(
    "originalPriceCents",
    originalPrice.ok ? originalPrice.cents : null,
    current.originalPriceCents,
  );
  put("dealPriceCents", dealPrice.ok ? dealPrice.cents : null, current.dealPriceCents);
  put("valueNotes", optionalText(form.valueNotes), current.valueNotes);
  put("couponCode", optionalText(form.couponCode), current.couponCode);
  put("limitations", optionalText(form.limitations), current.limitations);
  put("isExclusive", form.isExclusive, current.isExclusive);

  return { ok: true, patch, changedFields };
}
